import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Minus, BarChart3, RefreshCw } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

interface MarketAnalysis {
  market_price_min: number | null;
  market_price_max: number | null;
  market_price_avg: number | null;
  price_position: 'below_market' | 'market_average' | 'above_market';
  similar_properties_count: number;
  price_suggestion: string;
  competitive_advantage: string;
  last_analyzed_at: string;
}

interface PropertyMarketAnalysisProps {
  propertyId: string;
  autoAnalyze?: boolean;
}

export const PropertyMarketAnalysis: React.FC<PropertyMarketAnalysisProps> = ({ 
  propertyId, 
  autoAnalyze = false 
}) => {
  const [marketAnalysis, setMarketAnalysis] = useState<MarketAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  const fetchMarketAnalysis = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('property_market_analysis')
        .select('*')
        .eq('property_id', propertyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching market analysis:', error);
        return;
      }

      if (data) {
        setMarketAnalysis({
          market_price_min: data.market_price_min,
          market_price_max: data.market_price_max,
          market_price_avg: data.market_price_avg,
          price_position: (data.price_position as 'below_market' | 'market_average' | 'above_market') || 'market_average',
          similar_properties_count: data.similar_properties_count || 0,
          price_suggestion: data.price_suggestion || '',
          competitive_advantage: data.competitive_advantage || '',
          last_analyzed_at: data.last_analyzed_at
        });
      } else if (autoAnalyze) {
        await analyzeMarket();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeMarket = async () => {
    try {
      setIsAnalyzing(true);
      
      const { data, error } = await supabase.functions.invoke('analyze-market-prices', {
        body: { property_id: propertyId }
      });

      if (error) {
        console.error('Error analyzing market:', error);
        toast({
          title: "Erro na análise",
          description: "Não foi possível analisar o mercado",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setMarketAnalysis(data);
        toast({
          title: "Análise de mercado concluída",
          description: `${data.similar_properties_count} imóveis similares analisados`,
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erro",
        description: "Erro interno do servidor",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getPricePositionIcon = (position: string) => {
    switch (position) {
      case 'below_market':
        return <TrendingDown className="h-5 w-5 text-green-600" />;
      case 'above_market':
        return <TrendingUp className="h-5 w-5 text-red-600" />;
      default:
        return <Minus className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getPricePositionBadge = (position: string) => {
    switch (position) {
      case 'below_market':
        return <Badge className="bg-green-100 text-green-800">Abaixo do mercado</Badge>;
      case 'above_market':
        return <Badge className="bg-red-100 text-red-800">Acima do mercado</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">Na média</Badge>;
    }
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  useEffect(() => {
    fetchMarketAnalysis();
  }, [propertyId]);

  if (isLoading && !marketAnalysis) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-muted rounded w-1/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-muted rounded"></div>
            <div className="h-3 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Análise de Mercado
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={analyzeMarket}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isAnalyzing ? 'Analisando...' : 'Analisar'}
        </Button>
      </div>

      {marketAnalysis ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getPricePositionIcon(marketAnalysis.price_position)}
              <span className="font-medium">Posição no Mercado</span>
            </div>
            {getPricePositionBadge(marketAnalysis.price_position)}
          </div>

          {marketAnalysis.market_price_avg && (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-muted-foreground">Mínimo</div>
                <div className="font-semibold">
                  {formatCurrency(marketAnalysis.market_price_min)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-muted-foreground">Média</div>
                <div className="font-semibold text-lg">
                  {formatCurrency(marketAnalysis.market_price_avg)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-muted-foreground">Máximo</div>
                <div className="font-semibold">
                  {formatCurrency(marketAnalysis.market_price_max)}
                </div>
              </div>
            </div>
          )}

          {marketAnalysis.price_suggestion && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Sugestão de Preço</h4>
              <p className="text-sm text-blue-800">{marketAnalysis.price_suggestion}</p>
            </div>
          )}

          {marketAnalysis.competitive_advantage && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-2">Vantagem Competitiva</h4>
              <p className="text-sm text-green-800">{marketAnalysis.competitive_advantage}</p>
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{marketAnalysis.similar_properties_count} imóveis similares analisados</span>
            <span>
              Última análise: {new Date(marketAnalysis.last_analyzed_at).toLocaleString('pt-BR')}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-muted-foreground mb-4">
            Nenhuma análise de mercado disponível
          </div>
          <Button onClick={analyzeMarket} disabled={isAnalyzing}>
            {isAnalyzing ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <BarChart3 className="h-4 w-4 mr-2" />
            )}
            {isAnalyzing ? 'Analisando...' : 'Analisar mercado'}
          </Button>
        </div>
      )}
    </Card>
  );
};